import React, { useState, useEffect, useContext } from 'react';
import { KeycloakContext } from '../App';
import { GlobeAltIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { API_URL } from '../runtimeConfig';
import { useToast } from '../providers/ToastProvider';
import { useConfirm } from '../providers/ConfirmProvider';
import { PageHeader } from '../components/ui/page-header';
import { Skeleton } from '../components/ui/skeleton';

interface Domain {
  domain: string;
  users: string[];
}

interface IamUser {
  id: string;
  username: string;
  email: string;
}

export default function AdminDomains() {
  const ctx = useContext(KeycloakContext);
  const keycloak = ctx?.keycloak;
  const { toast } = useToast();
  const { confirm } = useConfirm();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [users, setUsers] = useState<IamUser[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddUserModal, setShowAddUserModal] = useState<boolean>(false);
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  useEffect(() => {
    fetchDomains();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const fetchDomains = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/admin/domains`, {
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch domains');
      const data = await response.json();
      setDomains(data || []);
      setError(null);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/users`, {
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      const data = await response.json();
      setUsers(data || []);
    } catch (err: any) {
      console.error('Error fetching users:', err);
    }
  };

  const handleAddUser = async () => {
    if (!selectedUserId || !selectedDomain) return;
    try {
      const response = await fetch(`${API_URL}/admin/domains/${selectedDomain.domain}/users`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${keycloak?.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ user_id: selectedUserId }),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to add user');
      }
      setShowAddUserModal(false);
      setSelectedUserId('');
      fetchDomains();
    } catch (err: any) {
      toast('Error adding user: ' + err.message, 'error');
    }
  };

  const handleRemoveUser = async (domain: string, userId: string) => {
    const ok = await confirm({ title: 'Remove user', message: 'Are you sure you want to remove this user from the domain?', confirmLabel: 'Remove', variant: 'danger' });
    if (!ok) return;
    try {
      const response = await fetch(`${API_URL}/admin/domains/${domain}/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${keycloak?.token}` },
      });
      if (!response.ok) throw new Error('Failed to remove user');
      fetchDomains();
    } catch (err: any) {
      toast('Error removing user: ' + err.message, 'error');
    }
  };

  const getUserInfo = (userId: string) => {
    const user = users.find((u) => u.id === userId);
    return user ? `${user.username} (${user.email})` : userId;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader
          icon={<GlobeAltIcon />}
          title="Domains"
          description="Manage domain ownership and user assignments"
        />
        <div className="rounded-lg border border-border bg-card p-6 space-y-3">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md bg-destructive/10 p-4">
        <p className="text-sm text-destructive">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<GlobeAltIcon />}
        title="Domains"
        description="Manage domain ownership and user assignments"
      />

      <div className="bg-card shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-muted/30">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Domain
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Assigned Users
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-card divide-y divide-border">
            {domains.length === 0 ? (
              <tr>
                <td colSpan={3} className="px-6 py-8 text-center text-sm text-muted-foreground">
                  No domains found.
                </td>
              </tr>
            ) : domains.map((domainItem) => (
              <tr key={domainItem.domain} className="hover:bg-muted/40">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                  {domainItem.domain}
                </td>
                <td className="px-6 py-4 text-sm text-muted-foreground">
                  {domainItem.users && domainItem.users.length > 0 ? (
                    <div className="space-y-1">
                      {domainItem.users.map((userId) => (
                        <div key={userId} className="flex items-center justify-between bg-muted rounded px-2 py-1">
                          <span className="text-xs">{getUserInfo(userId)}</span>
                          <button
                            onClick={() => handleRemoveUser(domainItem.domain, userId)}
                            className="ml-2 text-red-600 hover:text-red-900"
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground italic">No users assigned</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => {
                      setSelectedDomain(domainItem);
                      setShowAddUserModal(true);
                    }}
                    className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add User
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-background/70 backdrop-blur-sm transition-opacity" aria-hidden="true" onClick={() => setShowAddUserModal(false)} />
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-card rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-5">
                  <h3 className="text-lg leading-6 font-medium text-foreground" id="modal-title">
                    Add User to {selectedDomain?.domain}
                  </h3>
                  <div className="mt-4">
                    <label htmlFor="user-select" className="block text-left text-sm font-medium text-foreground mb-2">
                      Select User
                    </label>
                    <select
                      id="user-select"
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="block w-full border border-border rounded-md shadow-sm py-2 px-3 focus:outline-none focus-visible:ring-ring focus:border-primary sm:text-sm"
                    >
                      <option value="">Select a user...</option>
                      {users.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.username} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type="button"
                  onClick={handleAddUser}
                  disabled={!selectedUserId}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus-visible:ring-ring sm:col-start-2 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Add User
                </button>
                <button
                  type="button"
                  onClick={() => { setShowAddUserModal(false); setSelectedUserId(''); }}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-border shadow-sm px-4 py-2 bg-card text-base font-medium text-foreground hover:bg-muted/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus-visible:ring-ring sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
